import React from 'react';
import { Row, Col, Card, Badge, Button } from 'react-bootstrap';
import { StatCard, StatRow } from '../insights/InsightPage';
import { planKey, planLabel } from '../PlanFilter';
import { EMPTY, formatDate, formatNumber, plural } from '../../utils/format';
import { daysUntil, parsePlainDate, seatsOf } from './companyFormat';

/** Renewals this close (in days) are flagged on the overview. */
const RENEWAL_WARNING_DAYS = 30;

const pendingCount = (list) => (list.loaded ? list.data.filter((row) => row.status === 'PENDING').length : 0);

/** What staff should look at for this account, each with where to fix it. Empty when all is well. */
const attentionItems = (company, signupDomains, lockedSignupDomains, ipAddresses) => {
  const items = [];
  if (company.overduePayment) {
    items.push({ key: 'overdue', text: 'Payment is overdue.', tab: 'billing', anchor: 'billing-form', action: 'Open billing' });
  }
  const days = daysUntil(company.renewalDate);
  const renewal = formatDate(parsePlainDate(company.renewalDate));
  if (days !== null && days < 0) {
    items.push({ key: 'renewal', text: `The renewal date passed on ${renewal}.`, tab: 'billing', anchor: 'billing-form', action: 'Open billing' });
  } else if (days !== null && days <= RENEWAL_WARNING_DAYS) {
    items.push({
      key: 'renewal',
      text: days === 0 ? `Renews today, ${renewal}.` : `Renews in ${plural(days, 'day')}, on ${renewal}.`,
      tab: 'billing',
      anchor: 'billing-form',
      action: 'Open billing',
    });
  }
  const seats = seatsOf(company);
  if (seats > 0 && (company.totalUsers || 0) > seats) {
    items.push({
      key: 'seats',
      text: `${plural(company.totalUsers, 'user')} on ${plural(seats, 'seat')}.`,
      tab: 'billing',
      anchor: 'billing-form',
      action: 'Open billing',
    });
  }
  const signups = pendingCount(signupDomains);
  if (signups > 0) {
    items.push({ key: 'signup', text: `${plural(signups, 'sign-up domain')} waiting for approval.`, tab: 'config', anchor: 'config-signup', action: 'Open config' });
  }
  const locked = pendingCount(lockedSignupDomains);
  if (locked > 0) {
    items.push({ key: 'locked', text: `${plural(locked, 'locked sign-up domain')} waiting for approval.`, tab: 'config', anchor: 'config-signup', action: 'Open config' });
  }
  // Requests only show on Config while automatic check-in is on, so only count them then
  const ips = company.automaticOfficeCheckinEnabled ? pendingCount(ipAddresses) : 0;
  if (ips > 0) {
    items.push({
      key: 'ips',
      text: `${plural(ips, 'check-in IP address', 'check-in IP addresses')} waiting for approval.`,
      tab: 'config',
      anchor: 'config-checkin',
      action: 'Open config',
    });
  }
  return items;
};

const FEATURES = [
  { key: 'enableHolidayBooking', label: 'Holiday booking' },
  { key: 'deskBookingEnabled', label: 'Desk booking' },
  { key: 'lineManagementEnabled', label: 'Line management' },
  { key: 'whereaboutsApprovalsEnabled', label: 'Whereabouts approvals' },
  { key: 'calendarSyncEnabled', label: 'Calendar sync' },
  { key: 'weeklyNotesEnabled', label: 'Weekly notes' },
];

/** The Overview section: what needs attention, seats, and the account's key facts. */
const CompanyOverview = ({ company, signupDomains, lockedSignupDomains, ipAddresses, onOpenSection }) => {
  const items = attentionItems(company, signupDomains, lockedSignupDomains, ipAddresses);
  const seats = seatsOf(company);
  const totalUsers = company.totalUsers || 0;
  const overSeats = seats > 0 && totalUsers > seats;
  const approvedDomains = signupDomains.data.filter((d) => d.status === 'APPROVED').map((d) => d.domain);

  return (
    <>
      {items.length > 0 && (
        <Card className="shadow-sm mb-4">
          <Card.Header className="bg-light">
            <h5 className="mb-0">Needs attention</h5>
          </Card.Header>
          <Card.Body className="py-2">
            <ul className="list-unstyled mb-0">
              {items.map((item, i) => (
                <li
                  key={item.key}
                  className="d-flex flex-wrap justify-content-between align-items-center gap-2 py-2"
                  style={i < items.length - 1 ? { borderBottom: '1px solid var(--border-default)' } : undefined}
                >
                  <span>{item.text}</span>
                  <Button variant="link" size="sm" className="p-0" onClick={() => onOpenSection(item.tab, item.anchor)}>
                    {item.action}
                  </Button>
                </li>
              ))}
            </ul>
          </Card.Body>
        </Card>
      )}

      <StatRow>
        <StatCard
          xs={4}
          label="Users"
          value={formatNumber(totalUsers)}
          variant="primary"
          sub={seats > 0 ? (
            <span className={overSeats ? 'text-warning fw-semibold' : undefined}>
              {formatNumber(totalUsers)} of {plural(seats, 'seat')} used
            </span>
          ) : undefined}
        />
        <StatCard xs={4} label="Paid seats" value={formatNumber(company.paidUsers || 0)} />
        <StatCard xs={4} label="Free seats" value={formatNumber(company.freeUsers || 0)} />
      </StatRow>

      <Row className="g-4">
        <Col lg={6}>
          <Card className="shadow-sm h-100">
            <Card.Header className="bg-light">
              <h5 className="mb-0">Company information</h5>
            </Card.Header>
            <Card.Body>
              <dl className="kv-list">
                <dt>Company ID</dt>
                <dd><code>{company.id}</code></dd>
                <dt>Plan</dt>
                <dd>{planLabel(planKey(company))}</dd>
                <dt>Sign-up domains</dt>
                <dd>
                  {approvedDomains.join(', ') || EMPTY}
                  {signupDomains.data.some((d) => d.status === 'PENDING') && <Badge bg="warning" className="ms-2">Pending approval</Badge>}
                  {!company.domainNameSignupsEnabled && signupDomains.data.length > 0 && (
                    <Badge bg="secondary" className="ms-2" title="Domain sign-ups are switched off on Config">Sign-ups off</Badge>
                  )}
                </dd>
                <dt>Azure tenant ID</dt>
                <dd>{company.azureTenantId ? <code>{company.azureTenantId}</code> : EMPTY}</dd>
                <dt>Created</dt>
                <dd>{formatDate(company.createdAt)}</dd>
                <dt>Teams limit</dt>
                <dd>{formatNumber(company.numberOfTeamsLimit)}</dd>
              </dl>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={6}>
          <Card className="shadow-sm h-100">
            <Card.Header className="bg-light">
              <h5 className="mb-0">Features</h5>
            </Card.Header>
            <Card.Body>
              <dl className="kv-list">
                {FEATURES.map((feature) => (
                  <React.Fragment key={feature.key}>
                    <dt>{feature.label}</dt>
                    <dd>
                      <Badge bg={company[feature.key] ? 'success' : 'secondary'}>
                        {company[feature.key] ? 'On' : 'Off'}
                      </Badge>
                    </dd>
                  </React.Fragment>
                ))}
              </dl>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default CompanyOverview;
